import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { catchError, throwError } from 'rxjs';
import { AttributeType } from '../../enums';
import { Field, FormValues, RequiredField, SelectedUnits } from '../../models';
import { DynamicFormBaseService } from '../../services/dynamic-form-base.service';
import { NotificationService, NotificationType } from '../../services/notification.service';
import { ProductsService } from '../../services/products.service';
import { UnitConversionService } from '../../services/unit-conversion.service';

@Component({
  selector: 'app-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss'],
  standalone: false,
})
export class DynamicFormComponent implements OnInit, OnChanges {
  public FormInputs = AttributeType;
  @Input() formFields!: Field[];
  @Input() tab!: string;
  @Input() defaultModelName!: string;
  @Output() formValueChanged = new EventEmitter<{
    formValues: FormValues;
    requiredFields: RequiredField[];
    selectedUnits: SelectedUnits;
  }>();
  dynamicForm!: FormGroup;
  warningMessage: string = '';
  showWarning: boolean = false;
  selectedUnits: SelectedUnits = {};
  convertedMinMax: { [key: string]: { min: number; max: number } } = {};
  originalMinMax: { [key: string]: { min: number; max: number } } = {};

  constructor(
    private productService: ProductsService,
    private unitConversionservice: UnitConversionService,
    private translate: TranslateService,
    private notificationService: NotificationService,
    private formBaseService: DynamicFormBaseService,
  ) {
    // this.translate.setDefaultLang('en');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['formFields'] && !changes['formFields'].isFirstChange()) {
      this.initializeForm();
    }
    if (changes['tab'] && !changes['tab'].isFirstChange()) {
      this.patchFormValues();
    }
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm(): void {
    // Initialize condition form values from model form values if needed
    this.productService.initializeConditionFormValues();

    const group = this.createFormGroup();
    this.dynamicForm = new FormGroup(group);
    this.setupFormValueChanges();
    this.patchFormValues();
    // Emit the (possibly restored) values once on init. Rebuilding the form with stored values
    // does not fire valueChanges, so without this the parent never re-evaluates whether all
    // required fields are present — leaving the Submit button disabled when returning to the tab.
    this.emitCurrentFormState();
  }

  private emitCurrentFormState(): void {
    if (this.formFields?.length) {
      this.handleFormValueChanges(this.dynamicForm.value);
    }
  }

  private createFormGroup(): { [key: string]: FormControl } {
    const group: { [key: string]: FormControl } = {};
    const storedFieldValues = this.productService.getFormFieldValues();
    if (!this.formFields?.length) {
      return group;
    }
    this.formFields.forEach((field: Field) => {
      const validators = this.formBaseService.buildValidators(field);
      const initialValue = storedFieldValues[field.name] !== undefined ? storedFieldValues[field.name] : field.value;
      group[field.name] = new FormControl(initialValue, {
        validators: validators,
      });
      this.formBaseService.initializeUnitIfNeeded(field, this.selectedUnits);
      this.formBaseService.storeOriginalMinMax(field, this.originalMinMax);
    });
    return group;
  }

  private setupFormValueChanges(): void {
    this.dynamicForm.valueChanges
      .pipe(
        catchError((error) => {
          this.notificationService.showNotification(
            NotificationType.Error,
            this.translate.instant('ERROR_MESSAGES.ERROR_DURING_VALUE_CHANGES'),
          );
          return throwError(error);
        }),
      )
      .subscribe((formValues: FormValues) => {
        this.handleFormValueChanges(formValues);
      });
  }

  private handleFormValueChanges(formValues: FormValues): void {
    this.productService.updateFormFieldValues(this.dynamicForm.value);
    const requiredFields = this.getRequiredFields();
    this.formBaseService.convertFormValuesToBaseUnits(formValues, this.formFields, this.selectedUnits).then((valuesInBaseUnits) => {
      this.updateFormState(formValues);
      this.formBaseService.checkTemperatures(formValues, this.selectedUnits, this.formFields).subscribe((showWarning) => {
        this.showWarning = showWarning;
        this.emitFormChanges(valuesInBaseUnits, requiredFields);
      });
    });
  }

  private getRequiredFields(): RequiredField[] {
    return this.formFields
      .filter((field: Field) => field.required)
      .map((field: Field) => ({
        name: field.name,
        value: this.dynamicForm.get(field.name)?.value,
        min: this.originalMinMax[field.name]?.min,
        max: this.originalMinMax[field.name]?.max,
      }));
  }

  private updateFormState(formValues: FormValues): void {
    const combinedState = {
      formValues: formValues,
      selectedUnits: this.selectedUnits,
    };
    this.productService.setFormStateCondition(combinedState);
  }

  private emitFormChanges(valuesInBaseUnits: FormValues, requiredFields: RequiredField[]): void {
    this.formValueChanged.emit({
      formValues: valuesInBaseUnits,
      requiredFields: requiredFields,
      selectedUnits: this.selectedUnits,
    });
  }

  private patchFormValues(): void {
    const savedValues = this.productService.getFormStateCondition();
    const storedFieldValues = this.productService.getFormFieldValues();
    if (Object.keys(storedFieldValues).length > 0) {
      this.dynamicForm.patchValue(storedFieldValues);
    }
    if (savedValues && Object.keys(savedValues).length > 0) {
      const { selectedUnits } = savedValues;
      this.selectedUnits = selectedUnits;
      this.formFields.forEach((field: Field) => {
        if (this.convertedMinMax[field.name]) {
          field.min = this.convertedMinMax[field.name]?.min;
          field.max = this.convertedMinMax[field.name]?.max;
          this.formBaseService.updateValidators(field.name, field.min, field.max, this.dynamicForm);
        } else if (this.originalMinMax[field.name] && field.baseUnit && this.selectedUnits[field.name] && field.category) {
          field.min = this.originalMinMax[field.name]?.min;
          field.max = this.originalMinMax[field.name]?.max;
          this.formBaseService.convertMinMax(
            field,
            field?.baseUnit,
            this.selectedUnits[field.name],
            field?.category,
            this.originalMinMax,
            this.convertedMinMax,
            this.dynamicForm,
          );
        }
      });
    }
  }

  onUnitChange(event: Event, field: Field): void {
    this.formBaseService.onUnitChange(event, field, this.dynamicForm, this.selectedUnits, this.originalMinMax, this.convertedMinMax);
  }

  onInputChange(event: Event, field: Field) {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    // Only convert to number when the field loses focus
    if (value && !isNaN(parseFloat(value))) {
      this.dynamicForm.get(field.name)?.setValue(parseFloat(value));
    }
  }
}
